import { createAsyncLimiter, isTransferCancelled } from "@istari-frontend/shared";
import { type ContentToken, TransferCancelledError, type TransferEvent } from "@istari/istari-client";

import { uploadQueue } from "./file-transfer-config";

export class UploadFileMismatchError extends Error {
  override name = "UploadFileMismatchError";
}

export type UploadIntent = "user" | "generated";
export type UploadSnapshot =
  | {
      state: "pending" | "success" | "cancelled";
      phase?: never;
      bytesDone?: never;
      bytesTotal?: never;
      canResume?: never;
    }
  | {
      state: "uploading";
      phase?: "hash" | "transfer" | "finalizing" | "metadata";
      bytesDone?: number;
      bytesTotal?: number | null;
      canResume?: never;
    }
  | { state: "error"; canResume: boolean; phase?: never; bytesDone?: never; bytesTotal?: never };
export type UploadContext = {
  signal: AbortSignal;
  salt: string;
  token?: ContentToken;
  onProgress: (event: TransferEvent) => void;
  captureToken: (token: ContentToken) => void;
  metadata: () => void;
};
type UploadRecord = {
  file?: File;
  controller?: AbortController;
  salt: string;
  token?: ContentToken;
  intent: UploadIntent;
  snapshot: UploadSnapshot;
  onStart?: (operationId: string) => void;
  listeners: Set<(snapshot: UploadSnapshot) => void>;
  lastProgress: number;
  running: boolean;
  dispatched: boolean;
  preparedSnapshot?: UploadSnapshot;
  committing: boolean;
  identity: { name: string; size: number; lastModified: number };
};
export function registerUploadPayload(
  file: File,
  onStart?: (operationId: string) => void,
  intent: UploadIntent = "user",
): string {
  const id = crypto.randomUUID();
  payloads.set(id, {
    file,
    onStart,
    intent,
    controller: new AbortController(),
    salt: crypto.randomUUID(),
    snapshot: { state: "pending" },
    listeners: new Set(),
    lastProgress: 0,
    running: false,
    dispatched: false,
    committing: false,
    identity: { name: file.name, size: file.size, lastModified: file.lastModified },
  });
  return id;
}
export function registerGeneratedUpload(file: File): string {
  return registerUploadPayload(file, undefined, "generated");
}
export function isGeneratedUpload(id: string): boolean {
  return payloads.get(id)?.intent === "generated";
}
export function subscribeUpload(id: string, listener: (snapshot: UploadSnapshot) => void) {
  const record = payloads.get(id);
  record?.listeners.add(listener);
  if (record) listener(record.snapshot);
  return () => {
    record?.listeners.delete(listener);
  };
}
export function subscribeUploadEligibility(id: string, listener: (eligible: boolean) => void) {
  if (!canResumeUpload(id)) {
    listener(false);
    return () => undefined;
  }
  const listeners = eligibilityListeners.get(id) ?? new Set<EligibilityListener>();
  const entry: EligibilityListener = { notify: listener };
  eligibilityListeners.set(id, listeners);
  listeners.add(entry);
  listener(true);
  return () => {
    entry.notify = undefined;
    listeners.delete(entry);
    if (listeners.size === 0 && eligibilityListeners.get(id) === listeners) eligibilityListeners.delete(id);
  };
}
export function cancelUpload(id: string) {
  const record = payloads.get(id);
  if (record && !record.committing) record.controller?.abort();
}
export function forgetUpload(id: string) {
  const record = payloads.get(id);
  if (record?.running || record?.file || record?.preparedSnapshot) return;
  removeUpload(id);
}
export async function prepareUploadResume(id: string, file?: File) {
  const record = payloads.get(id);
  if (!canResumeUpload(id) || !record || record.running || !file || !record.token) {
    throw new Error("Choose the original file to resume");
  }
  if (
    file.name !== record.identity.name ||
    file.size !== record.identity.size ||
    file.lastModified !== record.identity.lastModified
  ) {
    throw new UploadFileMismatchError("Choose the original file to resume");
  }
  record.preparedSnapshot = record.snapshot;
  revokeEligibility(id);
  record.controller = new AbortController();
  record.file = file;
  publish(record, { state: "pending" });
}
export function releasePreparedUploadResume(id: string): boolean {
  const record = payloads.get(id);
  if (!record?.preparedSnapshot || record.dispatched) return false;
  record.controller?.abort();
  record.controller = undefined;
  record.file = undefined;
  const snapshot = record.preparedSnapshot;
  record.preparedSnapshot = undefined;
  publish(record, snapshot);
  grantEligibility(id);
  return true;
}
export function getUploadSessionVersion(): number {
  return sessionVersion;
}
export function subscribeUploadSessionClear(listener: () => void) {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}
export function clearUploadSession() {
  sessionVersion += 1;
  payloads.forEach((record, id) => {
    revokeEligibility(id);
    record.listeners.clear();
    Object.assign(record, { onStart: undefined, token: undefined, preparedSnapshot: undefined, file: undefined });
    if (!record.committing) record.controller?.abort();
  });
  payloads.clear();
  sessionListeners.forEach((listener) => listener());
}
export function runUploadPayload<T>(
  id: string,
  upload: (file: File, context: UploadContext) => Promise<T>,
): Promise<T> {
  const record = payloads.get(id);
  if (!record?.controller || !record.file || record.dispatched) {
    return Promise.reject(new Error("Upload is unavailable"));
  }
  record.dispatched = true;
  record.preparedSnapshot = undefined;
  revokeEligibility(id);
  const controller = record.controller;
  const originalToken = record.token;
  const queue = record.intent === "generated" ? generatedQueue : uploadQueue;
  return queue
    .run(async () => {
      record.running = true;
      try {
        if (controller.signal.aborted) throw new TransferCancelledError();
        record.onStart?.(id);
        record.onStart = undefined;
        publish(record, { state: "uploading" });
        record.token = undefined;
        const result = await upload(record.file!, {
          signal: controller.signal,
          salt: record.salt,
          token: originalToken,
          captureToken: (token) => {
            if (payloads.get(id) === record) record.token = { ...token };
          },
          metadata: () => {
            if (controller.signal.aborted) throw new TransferCancelledError();
            record.committing = true;
            publish(record, { state: "uploading", phase: "metadata" });
          },
          onProgress: (event) => {
            if (!record.running || record.controller !== controller || payloads.get(id) !== record) return;
            const now = Date.now();
            if (
              event.phase !== record.snapshot.phase ||
              now - record.lastProgress >= 100 ||
              event.kind === "complete"
            ) {
              record.lastProgress = now;
              publish(record, {
                state: "uploading",
                phase: event.kind === "complete" && event.phase === "transfer" ? "finalizing" : event.phase,
                bytesDone: event.bytesDone,
                bytesTotal: event.bytesTotal,
              });
            }
          },
        });
        publish(record, { state: "success" });
        return result;
      } catch (error) {
        if (error instanceof UploadFileMismatchError && payloads.get(id) === record) record.token = originalToken;
        const cancelled = controller.signal.aborted || isTransferCancelled(error);
        const eligible =
          !cancelled &&
          !record.committing &&
          record.intent === "user" &&
          Boolean(record.token) &&
          payloads.get(id) === record;
        if (eligible) grantEligibility(id);
        publish(record, cancelled ? { state: "cancelled" } : { state: "error", canResume: eligible });
        throw error;
      } finally {
        record.file = undefined;
        record.controller = undefined;
        record.onStart = undefined;
        record.running = false;
        record.dispatched = false;
        record.listeners.clear();
        if (!canResumeUpload(id)) removeUpload(id);
      }
    }, controller.signal)
    .catch((error) => {
      if (!record.running && record.file) {
        publish(record, { state: "cancelled" });
        record.file = undefined;
        record.controller = undefined;
        record.onStart = undefined;
        record.listeners.clear();
        removeUpload(id);
      }
      throw error;
    });
}
export function canResumeUpload(id: string): boolean {
  return eligibleErrors.has(id);
}
export function hasUploadPayload(id: string): boolean {
  return Boolean(payloads.get(id)?.file);
}

const generatedQueue = createAsyncLimiter(2);
const payloads = new Map<string, UploadRecord>();
const eligibleErrors = new Map<string, number>();
type EligibilityListener = { notify?: (eligible: boolean) => void };
const eligibilityListeners = new Map<string, Set<EligibilityListener>>();
const sessionListeners = new Set<() => void>();
let eligibilitySequence = 0;
let sessionVersion = 0;
window.addEventListener("pagehide", () => {
  payloads.forEach((record, id) => {
    if (!record.file && !record.running) removeUpload(id);
  });
});
function grantEligibility(id: string) {
  eligibleErrors.delete(id);
  eligibleErrors.set(id, ++eligibilitySequence);
  while (eligibleErrors.size > 100) {
    const oldest = eligibleErrors.keys().next().value;
    if (oldest !== undefined) removeUpload(oldest);
  }
}
function revokeEligibility(id: string) {
  eligibleErrors.delete(id);
  const record = payloads.get(id);
  if (record?.snapshot.state === "error" && record.snapshot.canResume) {
    publish(record, { state: "error", canResume: false });
  }
  const listeners = eligibilityListeners.get(id);
  eligibilityListeners.delete(id);
  listeners?.forEach((entry) => {
    const notify = entry.notify;
    Object.assign(entry, { notify: undefined });
    notify?.(false);
  });
  listeners?.clear();
}
function removeUpload(id: string) {
  revokeEligibility(id);
  payloads.delete(id);
}
function publish(record: UploadRecord, snapshot: UploadSnapshot) {
  Object.assign(record, { snapshot });
  record.listeners.forEach((listener) => listener(snapshot));
}
